import {
  Box,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Dashboard/Header";
import { useState } from "react";
import axios from "axios";

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const normalizeKeys = (row) => {
  const cleaned = {};

  for (const key in row) {
    cleaned[key.trim()] = row[key];
  }

  return cleaned;
};

const defaultBuildRequestBody = ({ idEquipo, fechaInicio, fechaFinal }) => ({
  idEquipo,
  fechaInicio,
  fechaFinal,
});

const ConsultaBase = ({ config }) => {
  const {
    title,
    endpoint,
    columns,
    mapRow,
    buildRequestBody = defaultBuildRequestBody,
    fetchRows,
  } = config;

  const [idEquipo, setIdEquipo] = useState("");
  const [fechaInicio, setFechaInicio] = useState(getCurrentDateTimeLocal());
  const [fechaFinal, setFechaFinal] = useState(getCurrentDateTimeLocal());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [openError, setOpenError] = useState(false);

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      height: "45px",
      "& fieldset": { borderColor: "#771726" },
      "&:hover fieldset": { borderColor: "#771726" },
      "&.Mui-focused fieldset": { borderColor: "#771726" },
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      color: "#000000",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#000000",
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (new Date(fechaFinal) < new Date(fechaInicio)) {
      setErrorMsg("La fecha final no puede ser anterior a la fecha de inicio.");
      setOpenError(true);
      return;
    }

    setLoading(true);
    setRows([]);

    try {
      let extractedData;

      if (fetchRows) {
        extractedData = await fetchRows({
          endpoint,
          idEquipo,
          fechaInicio,
          fechaFinal,
        });
      } else {
        const requestBody = buildRequestBody({
          idEquipo,
          fechaInicio,
          fechaFinal,
        });

        const response = await axios.post(endpoint, requestBody, {
          headers: { "Content-Type": "application/json" },
        });

        const parsedBody = response.data.body
          ? typeof response.data.body === "string"
            ? JSON.parse(response.data.body)
            : response.data.body
          : response.data;

        if (!parsedBody.success) {
          throw new Error(parsedBody.message || "La consulta no fue exitosa.");
        }

        extractedData = parsedBody.data;
      }

      if (
        Array.isArray(extractedData) &&
        extractedData.length > 0 &&
        Array.isArray(extractedData[0])
      ) {
        extractedData = extractedData[0];
      }

      if (!Array.isArray(extractedData)) {
        throw new Error("La API no regresó los datos en formato de tabla.");
      }

      const processedRows = extractedData.map((row, index) =>
        mapRow(normalizeKeys(row), index)
      );

      setRows(processedRows);
    } catch (error) {
      console.error("❌ Error en la solicitud:", error);
      setErrorMsg(error.message || "Error al consultar los datos. Intenta de nuevo.");
      setOpenError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        paddingTop: "90px",
        paddingX: "10px",
        overflowX: "hidden",
      }}
    >
      <Header title={title} sxTitle={{ fontSize: "2rem" }} />

      <Box
        component="form"
        onSubmit={handleSubmit}
        display="flex"
        gap={2}
        mb={2}
        justifyContent="center"
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <TextField
          label="ID Equipo"
          value={idEquipo}
          onChange={(e) => setIdEquipo(e.target.value)}
          required
          sx={{
            ...textFieldStyles,
            width: { xs: "100%", sm: 220 },
          }}
        />

        <TextField
          label="Fecha Inicio"
          type="datetime-local"
          value={fechaInicio}
          onChange={(e) => setFechaInicio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          required
          sx={{
            ...textFieldStyles,
            width: { xs: "100%", sm: 230 },
          }}
        />

        <TextField
          label="Fecha Final"
          type="datetime-local"
          value={fechaFinal}
          onChange={(e) => setFechaFinal(e.target.value)}
          InputLabelProps={{ shrink: true }}
          required
          sx={{
            ...textFieldStyles,
            width: { xs: "100%", sm: 230 },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: "#771726",
            color: "white",
            width: { xs: "100%", sm: "auto" },
            height: "45px",
            px: 3,
            whiteSpace: "nowrap",
            "&:hover": {
              bgcolor: "#747274",
            },
          }}
        >
          {loading ? "Consultando..." : "Consultar"}
        </Button>
      </Box>

      <Box
        mt="20px"
        width="100%"
        sx={{
          paddingBottom: "110px",
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: "#771726",
            color: "#ffffff",
          },
          "& .MuiTablePagination-root": {
            color: "#ffffff",
          },
          "& .MuiSvgIcon-root": {
            color: "#ffffff",
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          components={{
            LoadingOverlay: () => (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                }}
              >
                <CircularProgress sx={{ color: "#4d4a4a" }} />
              </Box>
            ),
          }}
          disableColumnMenu
          disableSelectionOnClick
          pageSize={10}
          rowsPerPageOptions={[10]}
          sx={{
            height: "600px",
            "& .MuiDataGrid-cell": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiDataGrid-columnHeaders": {
              position: "sticky",
              top: 0,
              backgroundColor: "#771726",
              zIndex: 1,
            },
          }}
        />
      </Box>

      <Snackbar
        open={openError}
        autoHideDuration={4500}
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenError(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsultaBase;